import { getStudioImage } from './studioAssets';
import { getDahuangImage } from './dahuangAssets';

// 光年切片工作室官网文案与作品目录。
// 房车相关链接沿用 siteConfig 的对外地址；大荒为站内预告页。

export const studioMeta = {
  name: '光年切片工作室',
  nameEn: 'LIGHTSLICE STUDIO',
  tagline: '用一辆房车、一座庄园，切开时代的横截面。',
  intro:
    '我们是一间专注沉浸式生存经营的独立游戏工作室。以极小的团队、辅以 AI 提升产能的方式，把「玩法框架、数值反馈与叙事张力」打磨成一款款可以反复游玩的作品。',
};

export const studioStats = [
  { value: '40 万+', label: '全平台累计下载' },
  { value: '30+ 篇', label: '官方更新日志' },
  { value: 'TapTap', label: '已上线平台' },
  { value: 'Steam', label: '即将发行' },
  { value: 'Google · Microsoft', label: '未来合作计划洽谈中' },
];

// 工作室理念 / 关于我们
export const studioPhilosophy = {
  eyebrow: 'ABOUT THE STUDIO',
  title: '小作坊，也要做有逻辑、有温度的降维打击',
  paragraphs: [
    '光年切片工作室以单人开发为核心、辅以 AI 协作的兼职摸索模式起步。我们相信，团队规模不该成为作品完成度的借口。',
    '从《末世：我有一辆房车》拿到 40 万+全平台下载开始，我们把每一条玩家评论、每一个建议，都当作继续熬夜写代码的动力。新作在玩法解耦、数值反馈和叙事沉浸上会走得更远。',
    '我们不玩虚的——大到用化学公式配化肥，小到用信息差搞期票做空，都尽量让它在具备游戏性的同时，有着符合逻辑的现实映射。',
  ],
};

export type StudioGame = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  status: 'live' | 'upcoming';
  statusLabel: string;
  tags: string[];
  cover: string;
  hero: string;
  href: string; // 站内跳转到对应游戏官网
  external: boolean;
  platforms: string[];
  accent: string; // 主题色，用于卡片氛围
};

export const studioGames: StudioGame[] = [
  {
    id: 'rebirth-hoarder',
    title: '末世：我有一辆房车',
    subtitle: '在停电、风雪与废墟之间，把一辆房车经营成最后的移动基地。',
    description:
      '重生、囤货、移动堡垒与微信叙事共同组成这个雪夜末世。炒股囤货、肉鸽重生、房车改装、卡牌战斗，还有一套 PC Web MOD 编辑器让你写下自己的废土故事。',
    status: 'live',
    statusLabel: '已上线 · 持续更新',
    tags: ['生存经营', '肉鸽', '卡牌战斗', '微信叙事'],
    cover: getStudioImage('rv_cover'),
    hero: getStudioImage('rv_hero'),
    href: '/rebirth-hoarder',
    external: false,
    platforms: ['TapTap 已上线', 'Steam 即将发行'],
    accent: '#4aa8ff',
  },
  {
    id: 'dahuang',
    title: '回到大荒',
    subtitle: '现代科技降维打击，把一场古代大饥荒，玩成最顶级的逆袭爽剧。',
    description:
      '大旱三年，赤地千里。用化学知识种红薯、用金融学做空粮商、用工程学筑水泥堡垒——从被地主欺压的流民，逆袭成拥有整座科技庄园的「天命仙师」。',
    status: 'upcoming',
    statusLabel: '即将上线',
    tags: ['生存经营', '科幻穿越', '策略建造', '知识降维'],
    cover: getDahuangImage('promo_1'),
    hero: getDahuangImage('hero'),
    href: '/dahuang',
    external: false,
    platforms: ['TapTap', '抖音小游戏'],
    accent: '#e0a94a',
  },
];

export const studioContact = {
  eyebrow: 'GET IN TOUCH',
  title: '与我们同行',
  description: '关注我们的作品，加入社区，或在测试服里再次看到你活跃的身影。',
  channels: [
    { label: '核心玩家 QQ 群', value: '1038765117', href: 'https://qm.qq.com/q/bHJNwfnZ72' },
    { label: '房车 TapTap 主页', value: 'taptap.cn/app/817986', href: 'https://l.taptap.cn/m5KMxZau?channel=rep-rep_xvonfbcgstt' },
    { label: '房车 Steam 商店', value: 'store.steampowered.com', href: 'https://store.steampowered.com/app/4663300/' },
  ],
};

// 工作室工具 / 生产力套件。独立部署在子路径，作为作品区延伸的工具入口。
export type StudioTool = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  tags: string[];
  cover: string;
  hero: string;
  href: string; // 子路径跳转到对应工具
  platforms: string[];
  accent: string;
};

export const studioTools: StudioTool[] = [
  {
    id: 'asset-tool',
    title: 'LightSlice 游戏美术资产工具台',
    subtitle: '一站式高精度抠图、UI 九宫格、PBR 贴图、精灵打包与骨骼动画工坊。',
    description:
      '针对独立游戏研发定制。提供纯色背景抠图与多主体自动分切、UI 九宫格拉伸标定、PBR 法线/高程贴图算解、精灵大合图打包、8-Bit 复古像素风、2D 骨骼形变动画、SVG 图标生成与音频处理，全部离线沙盒运行。',
    tags: ['抠图', '九宫格', 'PBR', '像素风', '骨骼动画'],
    cover: getStudioImage('rv_cover'),
    hero: getStudioImage('rv_hero'),
    href: '/assettool/',
    platforms: ['Web · 内置 AI 加速', '纯本地算力'],
    accent: '#6366f1',
  },
];

// 页脚法务与备案。备案号占位，上线大陆站点前请替换为真实 ICP 备案号。
export const studioFooter = {
  privacy: '/privacy-policy',
  agreement: '/user-agreement',
  icpPlaceholder: '暂未备案 · 待填写 ICP 备案号',
  copyright: `© ${new Date().getFullYear()} 光年切片工作室 LightSlice Studio`,
};
